'use client';

import { Lock, Crown, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PlanType, PLAN_DISPLAY_NAMES } from '@/lib/config/growthbook-features.config';

interface LockedFeatureProps {
  requiredPlan: PlanType;
  featureName: string;
  description?: string;
  showUpgradeButton?: boolean;
  onUpgradeClick?: () => void;
  className?: string;
  children?: React.ReactNode;
  variant?: 'overlay' | 'inline' | 'tooltip';
}

export function LockedFeature({
  requiredPlan,
  featureName,
  description,
  showUpgradeButton = false,
  onUpgradeClick,
  className,
  children,
  variant = 'overlay',
}: LockedFeatureProps) {
  const planName = PLAN_DISPLAY_NAMES[requiredPlan];
  const isPro = requiredPlan === 'pro';
  const isEnterprise = requiredPlan === 'enterprise';

  const handleUpgradeClick = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      // Default behavior - navigate to billing page
      window.location.href = '/dashboard/settings/billing';
    }
  };

  // Inline variant - simple badge with lock icon
  if (variant === 'inline') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={cn(
                'gap-1 cursor-help',
                isPro && 'border-apprendo-500/50 text-apprendo-600 dark:text-apprendo-400',
                isEnterprise && 'border-amber-500/50 text-amber-600 dark:text-amber-400',
                className
              )}
            >
              {isPro ? <Crown className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
              {planName}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">{featureName}</p>
            {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
            <p className="text-xs mt-2">Upgrade to {planName} to unlock</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Tooltip variant - show tooltip on hover
  if (variant === 'tooltip') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={cn('relative', className)}>{children}</div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {isPro ? (
                  <Crown className="h-4 w-4 text-apprendo-500" />
                ) : (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
                <p className="font-medium">{featureName}</p>
              </div>
              {description && <p className="text-sm text-muted-foreground">{description}</p>}
              <div className="flex items-center justify-between pt-2 border-t">
                <Badge variant="secondary" className="text-xs">
                  Requires {planName}
                </Badge>
                {showUpgradeButton && (
                  <Button
                    size="sm"
                    variant="link"
                    className="h-auto p-0 text-xs"
                    onClick={handleUpgradeClick}
                  >
                    Upgrade →
                  </Button>
                )}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Overlay variant - default, covers the content with a locked overlay
  return (
    <div className={cn('relative', className)}>
      {children}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
        <div className="text-center p-4 space-y-3">
          <div className="flex justify-center">
            {isPro ? (
              <div className="relative">
                <Crown className="h-8 w-8 text-apprendo-500" />
                <Sparkles className="h-4 w-4 text-apprendo-400 absolute -top-1 -right-1" />
              </div>
            ) : isEnterprise ? (
              <div className="relative">
                <Crown className="h-8 w-8 text-amber-500" />
                <Sparkles className="h-4 w-4 text-amber-400 absolute -top-1 -right-1" />
              </div>
            ) : (
              <Lock className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
          
          <div>
            <p className="font-medium text-sm">{featureName}</p>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </div>

          <Badge
            variant={isPro ? 'default' : 'secondary'}
            className={cn(
              'text-xs',
              isPro && 'bg-apprendo-500 hover:bg-apprendo-600',
              isEnterprise && 'bg-amber-500 hover:bg-amber-600'
            )}
          >
            {planName} Feature
          </Badge>

          {showUpgradeButton && (
            <Button
              size="sm"
              variant={isPro ? 'default' : 'outline'}
              className={cn(
                'text-xs',
                isPro && 'bg-apprendo-500 hover:bg-apprendo-600'
              )}
              onClick={handleUpgradeClick}
            >
              Upgrade to {planName}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Simplified overlay for radio button options
interface LockedFeatureOverlayProps {
  plan: PlanType;
  className?: string;
}

export function LockedFeatureOverlay({ plan, className }: LockedFeatureOverlayProps) {
  const planName = PLAN_DISPLAY_NAMES[plan];
  const isPro = plan === 'pro';
  
  return (
    <div
      className={cn(
        'absolute inset-0 bg-background/60 backdrop-blur-[2px] rounded-lg flex items-center justify-end pr-3 pointer-events-none z-10',
        className
      )}
    >
      <Badge
        variant="outline"
        className={cn(
          'gap-1 text-xs',
          isPro && 'border-apprendo-500/50 text-apprendo-600 dark:text-apprendo-400 bg-purple-50 dark:bg-purple-950/20'
        )}
      >
        {isPro ? <Crown className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
        {planName} only
      </Badge>
    </div>
  );
}